// What the punter asks for, counted — so a week after shipping it is possible to
// say whether they are getting it (server/tips-events.mjs has the field list).
//
// Buffered and sent in batches: on the tenth event, twenty seconds after the
// first, or when the page is hidden. A failed send is dropped rather than
// retried — this is a measurement, and a measurement that nags the network is
// worse than a gap in it. Nothing is sent in /demo or on the sample board, where
// nobody is asking for anything real.

import { apiBase } from '../api-base.js';
import { normaliseRequest, requestKey } from '../engine/ask.js';

export const FLUSH_AT = 10;
export const FLUSH_AFTER_MS = 20_000;

const queue = [];
let timer = null;
let enabled = () => false;
let surface = 'web';

/** Wire it up once, from the store: when it may send, and what device this is. */
export function configureAnalytics({ isEnabled, platform } = {}) {
  if (typeof isEnabled === 'function') enabled = isEnabled;
  if (platform) surface = platform;
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }
}

/** The request's shape, as the server keeps it: dimensions and weights, no ids. */
export function dimsOf(request) {
  const r = normaliseRequest(request);
  return {
    sports: r.sports.map((s) => ({ sport: s.sport, weight: s.weight })),
    events: r.events.map((e) => ({ weight: e.weight })),
    when: typeof r.when === 'string' ? r.when : 'custom',
    legsMin: r.legs.min,
    legsMax: r.legs.max,
    risk: r.risk ?? undefined,
    odds: !!r.odds,
    strategy: r.strategy,
    include: r.include.length,
    exclude: r.exclude.length,
  };
}

/** Count one thing. `request` becomes its key and its dimensions. */
export function track(type, { request = null, ...data } = {}) {
  if (!enabled()) return;
  queue.push({
    type,
    at: new Date().toISOString(),
    surface,
    ...(request ? { requestKey: requestKey(request), dims: dimsOf(request) } : {}),
    ...data,
  });
  if (queue.length >= FLUSH_AT) flush();
  else if (!timer) timer = setTimeout(flush, FLUSH_AFTER_MS);
}

/** Send what is queued. Never throws; never retries. */
export async function flush() {
  clearTimeout(timer);
  timer = null;
  if (!queue.length) return;
  const events = queue.splice(0, queue.length);
  try {
    const mod = await import('../../../addons/auth/index.js');
    const token = await mod.getIdToken?.();
    if (!token) return;
    await fetch(`${apiBase()}/api/tips-events`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ events }),
    });
  } catch {
    // Dropped, deliberately. See the note at the top.
  }
}

/** For tests: what is waiting to be sent. */
export const pending = () => queue.slice();
